import useScrollReveal from '../hooks/useScrollReveal';
import Seo from '../components/Seo';
import heroImage from '../file/pesticides/pesticide-sprayer-drone.jpg';

const tocItems = [
  { href: '#policy', label: '理念' },
  { href: '#image', label: '農薬のイメージ' },
  { href: '#why-use', label: '使う理由' },
  { href: '#law', label: '法律上の農薬' },
  { href: '#map', label: '分類の整理' },
  { href: '#natural', label: '自然由来の注意点' },
  { href: '#reserved', label: '保留資材' },
  { href: '#organic', label: '有機JASと表示' },
  { href: '#drsfarm', label: 'Drs. Farmの姿勢' },
  { href: '#sources', label: '参考資料' },
];

const currentSpecificPesticides = ['食酢', '重曹', '次亜塩素酸水', 'エチレン', '天敵'];

const reservedMaterials = [
  'インドセンダンの実・樹皮・葉',
  'ウエスタン・レッド・シーダー',
  '甘草',
  '酵母エキス等の混合液',
  '二酸化チタン',
  'ヒノキチオール・ヒバ油',
  'ヒノキの葉',
  'ホソバヤマジソ',
  '酒類（焼酎）',
  '木酢液・竹酢液',
];

const pesticideFreePractices = ['草取り', '太陽熱消毒', '防虫ネット', '天敵利用', '水管理', '土づくり', '品種選び', '記録と確認'];

export default function PesticidesInfoPage() {
  useScrollReveal();

  return (
    <>
      <Seo
        title="農薬に頼らない農業について"
        description="Drs. Farmの理念を起点に、農薬取締法、特定農薬、保留資材、有機JAS、特別栽培農産物の考え方を整理した解説ページです。"
        path="/pesticides"
        image={heroImage}
      />

      <nav className="pesticide-toc reveal" data-reveal aria-label="農薬ページの目次">
        {tocItems.map((item) => (
          <a key={item.href} href={item.href}>{item.label}</a>
        ))}
      </nav>

      <section
        className="pesticide-hero reveal"
        data-reveal
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(246, 246, 242, 0.96) 0%, rgba(246, 246, 242, 0.86) 48%, rgba(246, 246, 242, 0.28) 100%), url("${heroImage}")`,
        }}
      >
        <div className="pesticide-hero-copy">
          <p className="eyebrow">Pesticide Guide</p>
          <h1 className="pesticide-title">
            農薬に頼らない農業を、
            <br />
            正しく、誠実に広げるために。
          </h1>
          <p>
            Drs. Farmは、農薬に頼らない農業を推進し、安心安全な食生活を普及させることで、
            健康な人々を増やすことを理念としています。
          </p>
          <p>
            ただし、「農薬を使わない」と伝えるには、法律、表示、栽培現場の現実を丁寧に整理する必要があります。
            このページは、そのための入口です。
          </p>
        </div>
      </section>

      <section className="panel reveal pesticide-policy" data-reveal id="policy">
        <header className="section-head">
          <p className="number-label">01 POLICY</p>
          <h2 className="pesticide-heading">Drs. Farmの理念と目的</h2>
        </header>
        <div className="pesticide-policy-grid">
          <article>
            <h3>第1項 理念</h3>
            <p>
              Drs. Farmは、農薬に頼らない農業を推進し、安心安全な食生活を普及させることで、
              健康な人々を増やすことを理念とします。
            </p>
          </article>
          <article>
            <h3>第2項 目的</h3>
            <p>本団体は、医師と農家の双方の視点から、次の目的を持って活動します。</p>
            <ul>
              <li>医師による健康的な食生活についての提言と、第三者的立場での評価を行うこと。</li>
              <li>熱心な農家を支援し、安心安全な食生活を普及させること。</li>
              <li>農薬に頼らない農業を推進すること。</li>
            </ul>
          </article>
        </div>
      </section>

      <section className="numbered-story reveal pesticide-image-section" data-reveal id="image">
        <figure className="pesticide-photo-card">
          <img src={heroImage} alt="畑で使われる農薬散布用ドローンと散布機のイメージ" loading="lazy" />
          <figcaption>生成画像: 農薬散布機やドローンから想起される一般的な農薬のイメージ</figcaption>
        </figure>
        <div>
          <p className="number-label">02 IMAGE</p>
          <h2 className="pesticide-heading">農薬と聞いて、何を思い浮かべますか？</h2>
          <p>
            多くの人がまず思い浮かべるのは、農薬散布マシーンやドローン、あるいは化学合成された殺虫剤や除草剤かもしれません。
            もちろん、それらは農薬の代表的なイメージです。
          </p>
          <p>
            でも、日本の制度で考える「農薬」は、それだけではありません。病害虫や雑草を防ぐ目的で使うものは、
            化学合成か自然由来かに関係なく、農薬取締法との関係を確認する必要があります。
          </p>
        </div>
      </section>

      <section className="panel reveal pesticide-why-section" data-reveal id="why-use">
        <header className="section-head">
          <p className="number-label">03 WHY USE</p>
          <h2 className="pesticide-heading">そもそも、なぜ農薬を使うのでしょう？</h2>
          <p className="section-subcopy">
            農薬は、作物を病気や害虫、雑草から守るために使われます。虫は葉を齧り、実を傷つけ、
            病気は畑全体に広がることがあります。収穫量が落ちるだけでなく、見た目や保存性にも影響します。
          </p>
        </header>
        <div className="pesticide-why-grid">
          <article>
            <h3>葉を守る</h3>
            <p>葉が食べられると、植物は光合成をしにくくなり、育ちが弱くなることがあります。</p>
          </article>
          <article>
            <h3>実を守る</h3>
            <p>実が齧られたり傷ついたりすると、販売できなくなったり、傷みやすくなったりします。</p>
          </article>
          <article>
            <h3>病気の広がりを抑える</h3>
            <p>一つの株の病気が、雨や風、虫を通じて周囲へ広がることがあります。</p>
          </article>
        </div>
        <aside className="pesticide-note pesticide-note-compact">
          <h3>農薬に頼らない農家さんの大変さ</h3>
          <p>
            農薬に頼らずに作るということは、虫や病気と向き合わないという意味ではありません。
            見回り、手作業での除草、防虫ネット、袋かけ、剪定、土づくりなど、別の手間を積み重ねるということです。
            特に果物は、実そのものが虫や病気、傷の影響を受けやすく、きれいに育てる難しさが大きい作物です。
          </p>
          <p>
            さらに、虫食いや病気で収穫できる量が減ると、農家さんの収益も減ります。
            手間は増えるのに、出荷できる量や品質が安定しにくい。ここに、農薬に頼らない農業の現実的な難しさがあります。
          </p>
        </aside>
      </section>

      <section className="panel reveal pesticide-intro-grid" data-reveal id="law">
        <div>
          <p className="number-label">04 LAW</p>
          <h2 className="pesticide-heading">法律上での農薬とは？</h2>
          <p>
            農薬取締法では、農作物等を害する菌、線虫、だに、昆虫、ねずみ、雑草などの防除に用いられる殺菌剤、
            殺虫剤その他の薬剤や、農作物等の生理機能の増進・抑制に用いられる薬剤などが農薬として扱われます。
          </p>
          <p>
            つまり、判断の入口は「自然由来か、化学合成か」ではありません。何の目的で使うのか、
            登録や指定があるのか、作物や使用方法が基準に合っているのかが重要になります。
          </p>
        </div>
        <div className="pesticide-rule-card" aria-label="農薬に関係する判断要素">
          <span>目的</span>
          <strong>病害虫・雑草を防ぐため？</strong>
          <span>登録</span>
          <strong>登録農薬・特定農薬にあたる？</strong>
          <span>使い方</span>
          <strong>作物・量・時期・回数を守っている？</strong>
        </div>
      </section>

      <section className="panel reveal pesticide-map-section" data-reveal id="map">
        <header className="section-head">
          <p className="number-label">05 MAP</p>
          <h2 className="pesticide-heading">農薬の中にも、いくつかの分類があります。</h2>
          <p className="section-subcopy">
            ざっくり見ると、登録を受けた農薬、登録不要で使える特定農薬、判断が保留されている保留資材、
            そして登録や指定がないため農薬としては使えない資材があります。
          </p>
        </header>
        <div className="pesticide-venn" aria-label="農薬の分類図">
          <div className="pesticide-venn-ring pesticide-venn-ring-main">
            <span>農薬</span>
          </div>
          <article className="pesticide-venn-card pesticide-venn-registered">
            <h3>登録農薬</h3>
            <p>国の審査を受け、ラベルの使用基準に沿って使う農薬。</p>
          </article>
          <article className="pesticide-venn-card pesticide-venn-specific">
            <h3>特定農薬</h3>
            <p>登録を受けなくても、農薬として販売・使用できる指定資材。</p>
          </article>
          <article className="pesticide-venn-card pesticide-venn-reserved">
            <h3>保留資材</h3>
            <p>特定農薬にするかどうか、判断が保留されている資材。</p>
          </article>
          <article className="pesticide-venn-card pesticide-venn-unregistered">
            <h3>未登録・未指定</h3>
            <p>農薬としての効果をうたって販売・使用すると問題になり得る資材。</p>
          </article>
        </div>
      </section>

      <section className="numbered-story reveal pesticide-materials" data-reveal id="natural">
        <div>
          <p className="number-label">06 NATURAL</p>
          <h2 className="pesticide-heading">自然由来だったら、なんでもOK？</h2>
          <p>
            「自然由来だから安心」という表現は、気持ちとしては分かりやすい一方で、制度上も安全性の面でも注意が必要です。
            トリカブトも、マムシの毒も自然由来です。自然由来であることは、安全性を保証する言葉ではありません。
          </p>
          <p>
            木酢液、牛乳、焼酎、EM菌、納豆菌なども、使い方や目的によって農薬取締法との関係を確認する必要があります。
            農家の方でも、この境目を知らないまま「農薬に頼らない」と言っているケースがあります。
          </p>
        </div>
        <div className="pesticide-natural-card">
          <strong>自然由来</strong>
          <span>イコール</span>
          <strong>自由に使える</strong>
          <p>ではありません。</p>
        </div>
      </section>

      <section className="panel reveal pesticide-reserved-section" data-reveal id="reserved">
        <header className="section-head">
          <p className="number-label">07 RESERVED MATERIALS</p>
          <h2 className="pesticide-heading">そこから「保留資材」という考え方が生まれました。</h2>
          <p className="section-subcopy">
            2002年の農薬取締法改正後、約740種類の資材について情報提供がありました。
            検討の結果、特定農薬になったもの、検討対象から外れたもの、判断が保留されたものに分かれていきました。
          </p>
        </header>
        <div className="pesticide-reserved-grid">
          <div className="pesticide-large-number" aria-label="約740種類">
            <strong>約740</strong>
            <span>種類から検討が始まりました</span>
          </div>
          <div>
            <h3>2026年7月時点で確認している整理</h3>
            <p>
              愛知県が公開している資料では、「特定農薬の指定の検討対象とする資材一覧」として、
              次の10資材が掲載されています。これらが、いわゆる保留資材として残っているものと考えられます。
              ただし分類が確定した資材ではないため、今後の判断によって扱いが変わる可能性があります。
            </p>
            <div className="pesticide-reserved-list">
              {reservedMaterials.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
        </div>
        <aside className="pesticide-note pesticide-note-compact">
          <h3>保留資材の扱い</h3>
          <p>
            愛知県資料では、特定農薬の指定の検討対象とされた資材についても、農薬としての効能をうたっているものは
            取締の対象になる一方、効能をうたわず、使用者が自分の判断と責任で使う場合は取締の対象にならないと説明されています。
            石川県の農薬安全使用Q&Aでも、判定を保留された資材は使用者の自己責任の範囲で使用できると説明されています。
          </p>
          <p>
            補足として、Drs. Farmでは石川県へ電話確認を行い、保留資材については上記Q&Aと同様に、
            自己責任での使用は直ちに違法扱いではない旨を確認しています。
          </p>
        </aside>
      </section>

      <section className="panel reveal pesticide-specific" data-reveal>
        <header className="section-head">
          <p className="number-label">08 SPECIFIC PESTICIDES</p>
          <h2 className="pesticide-heading">現在の特定農薬は、かなり限られています。</h2>
          <p className="section-subcopy">
            特定農薬は、原材料に照らして農作物等、人畜、水産動植物に害を及ぼすおそれがないことが明らかなものとして、
            農林水産大臣と環境大臣が指定する農薬です。登録を受けなくても農薬として販売・使用できます。
          </p>
        </header>
        <div className="pesticide-specific-list">
          {currentSpecificPesticides.map((item) => (
            <article key={item}>
              <span>{item}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="panel reveal pesticide-organic" data-reveal id="organic">
        <header className="section-head">
          <p className="number-label">09 ORGANIC AND LABELING</p>
          <h2 className="pesticide-heading">有機JASも「農薬ゼロ」の制度ではありません。</h2>
          <p className="section-subcopy">
            有機JASは、化学的に合成された肥料や農薬の使用を避けることを基本にしつつ、
            規格で認められた農薬を使える仕組みです。種子の消毒、近隣圃場からの飛散、
            栽培前の履歴などもあり、「100％農薬なし」と言い切ることは現実的に難しい場面があります。
          </p>
        </header>
        <div className="pesticide-label-grid">
          <article>
            <h3>有機JAS</h3>
            <p>
              登録・認証には費用や事務負担がかかります。強いこだわりを持つ小規模農家でも、
              有機JASを取得していないことがあります。
            </p>
          </article>
          <article>
            <h3>特別栽培農産物</h3>
            <p>
              地域の慣行レベルに比べて、節減対象農薬の使用回数と化学肥料の窒素成分量を
              50％以下にして栽培された農産物です。
            </p>
          </article>
          <article>
            <h3>栽培期間中不使用</h3>
            <p>
              節減対象農薬を使用しなかった場合は、「節減対象農薬：栽培期間中不使用」と表示できます。
              「栽培期間中」と書くのは、種子・苗・周辺環境など、栽培現場には複数の要因があるためです。
            </p>
          </article>
        </div>
      </section>

      <section className="reveal pesticide-practice-section" data-reveal>
        <header className="section-head">
          <p className="number-label">10 PRACTICE</p>
          <h2 className="pesticide-heading">農薬に頼らない農業は、何もしない農業ではありません。</h2>
          <p className="section-subcopy">
            薬で雑草を枯らすのではなく、栽培方法によって雑草や病害虫を抑える。
            そのために、農家は観察と手間を積み重ねています。
          </p>
        </header>
        <div className="pesticide-practice-grid">
          {pesticideFreePractices.map((practice) => (
            <article key={practice}>
              <h3>{practice}</h3>
            </article>
          ))}
        </div>
      </section>

      <section className="maker-section reveal pesticide-bottom" data-reveal id="drsfarm">
        <div className="maker-copy">
          <p className="eyebrow">Drs. Farm Policy</p>
          <h2 className="pesticide-heading">
            Drs. Farmは、
            <br />
            栽培期間中に農薬に頼らない農家を応援します。
          </h2>
          <p>
            参加している農家さんには、農薬取締法や表示の考え方を丁寧に説明し、
            誤解や誤った運用が見つかった場合は改善を促します。
          </p>
          <p>
            私たちは「農薬を使っていない」という言葉だけを掲げるのではなく、
            法律を守り、記録を残し、環境や人への負担をできるだけ少なくする農業を広げていきます。
          </p>
          <p>
            Drs. Farmでは、団体の方針として、登録農薬を使用しない栽培を基本とします。
            もちろん、登録されていない資材を農薬のように使うことも認めません。
            病害虫や雑草を防ぐ目的で何かを使う場合は、農薬取締法との関係を確認し、
            法律に沿った範囲で、農薬に頼らない栽培を続けるための工夫と栽培記録の整備を大切にしています。
          </p>
        </div>
        <aside className="pesticide-note" aria-label="ページ全体の表現方針">
          <h3>このページの表現方針</h3>
          <p>
            個別資材について「違法」「農薬」と断定しすぎず、
            病害虫や雑草を防ぐ目的で使用する資材は、農薬取締法との関係を確認する必要がある、
            という表現を基本にしています。
          </p>
        </aside>
      </section>

      <section className="panel reveal pesticide-sources" data-reveal id="sources">
        <h2 className="pesticide-heading">参考資料</h2>
        <ul>
          <li><a href="https://laws.e-gov.go.jp/law/323AC0000000082" target="_blank" rel="noreferrer">農薬取締法（e-Gov法令検索）</a></li>
          <li><a href="https://www.maff.go.jp/j/nouyaku/" target="_blank" rel="noreferrer">農薬コーナー（農林水産省）</a></li>
          <li><a href="https://www.maff.go.jp/j/nouyaku/n_tokutei/about_tokutei.html" target="_blank" rel="noreferrer">特定農薬とは？（農林水産省）</a></li>
          <li><a href="https://www.maff.go.jp/j/nouyaku/n_tokutei/tokutei_kokuzi.html" target="_blank" rel="noreferrer">特定農薬を指定する件（農林水産省・環境省告示）</a></li>
          <li><a href="https://www.pref.aichi.jp/uploaded/attachment/594460.pdf" target="_blank" rel="noreferrer">特定農薬（特定防除資材）とは（愛知県 PDF）</a></li>
          <li><a href="https://jppa.or.jp/archive/pdf/68_08_39.pdf" target="_blank" rel="noreferrer">特定農薬（特定防除資材）の指定及び検討の現状について（植物防疫・農林水産省農薬対策室）</a></li>
          <li><a href="https://www.pref.ishikawa.lg.jp/nousei/brand/oshirase/nouyaku-kaisei.html" target="_blank" rel="noreferrer">農薬安全使用Q&A（石川県）</a></li>
          <li><a href="https://www.maff.go.jp/j/jas/jas_kikaku/attach/pdf/yuuki-437.pdf" target="_blank" rel="noreferrer">有機農産物の日本農林規格 JAS 1605:2024（農林水産省）</a></li>
          <li><a href="https://www.maff.go.jp/j/jas/jas_kikaku/tokusai_a.html" target="_blank" rel="noreferrer">特別栽培農産物に係る表示ガイドライン（農林水産省）</a></li>
        </ul>
      </section>
    </>
  );
}
